"use client"

import { Auth0Provider } from "@auth0/auth0-react"
import { useEffect, useState, PropsWithChildren } from "react"
import { loadAuth0Config, type Auth0Config } from "@/lib/auth"
import { setLocalDevMode } from "@/hooks/useAuth"
import { detectFrontendMode, shouldUseNoAuth } from "@/lib/agentcore-client/config"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import { Item, ItemContent, ItemTitle } from "@/components/ui/item.tsx"

const AuthProvider = ({ children }: PropsWithChildren) => {
  const [authConfig, setAuthConfig] = useState<Auth0Config | null>(null)
  const [loading, setLoading] = useState(true)
  const [localMode, setLocalMode] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  useEffect(() => {
    async function init() {
      try {
        // No_Auth_Mode is entered only when all three conditions hold together:
        // the run is Normal_Compose_Mode, a non-empty Local_URL_Map is present,
        // and no Auth0 domain is configured. Deployed modes (Vite_Deployed_Mode,
        // Deployed_Build) always require Auth0 even if a local map is checked in.
        const response = await fetch("/aws-exports.json")
        if (response.ok) {
          const config = await response.json()
          const mode = detectFrontendMode(import.meta.env)
          const auth0Domain = config.auth0Domain || import.meta.env.VITE_AUTH0_DOMAIN || ""
          if (
            shouldUseNoAuth({
              mode,
              localAgentUrls: config.localAgentUrls,
              auth0Domain,
            })
          ) {
            setLocalMode(true)
            setLoading(false)
            return
          }
        }

        const config = await loadAuth0Config()
        setAuthConfig(config)
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unknown error"
        console.error("Failed to load Auth0 configuration:", err)
        setError(message)
      } finally {
        setLoading(false)
      }
    }
    init()
  }, [])
  if (loading) {
    return (
      <div className="h-svh">
        <LoadingSpinner loadingContent="Loading authentication configuration..."/>
      </div>
    )
  }

  if (error) {
    return (
      <div className=" flex flex-col justify-center h-full">
        <div className="flex align-middle justify-center w-full">
          <Item variant="outline" className="w-auto">
            <ItemContent>
              <ItemTitle className="text-destructive">Error: {error}</ItemTitle>
            </ItemContent>
          </Item>
        </div>
      </div>
    )
  }

  // Local development mode - skip Auth0
  if (localMode) {
    setLocalDevMode(true)
    return <>{children}</>
  }

  if (!authConfig || !authConfig.domain || !authConfig.clientId) {
    return (
      <div className="flex items-center justify-center min-h-screen text-xl">
        Failed to load authentication configuration. Check Auth0 settings.
      </div>
    )
  }

  return (
    <Auth0Provider
      domain={authConfig.domain}
      clientId={authConfig.clientId}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      authorizationParams={{
        redirect_uri: authConfig.redirectUri,
        audience: authConfig.audience,
        scope: "openid profile email offline_access",
      }}
    >
      {children}
    </Auth0Provider>
  )
}

export { AuthProvider }
